import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import './AdminDashboard.css';

import useDashboardData from './hooks/useDashboardData';
import { STRINGS } from './utils/constants';

import DashboardHeader from './components/DashboardHeader';
import FilterPanel from './components/FilterPanel';
import PeriodNavigator from './components/PeriodNavigator';
import ServiceFilterStrip, { ALL_SERVICES } from './components/ServiceFilterStrip';
import KPIGrid from './components/KPIGrid';
import MainChart from './components/MainChart';
import PaymentDistribution from './components/PaymentDistribution';
import TrendChart from './components/TrendChart';
import AdditionalAnalytics from './components/AdditionalAnalytics';
import { EmptyState, ErrorState, IdleState, LoadingState } from './components/StateView';

const THEME_STORAGE_KEY = 'dashboard-theme';

// ─────────────────────────────────────────────────────────────────────────────
// AdminDashboard
// Thin orchestrator: wires the data hook to UI sections and picks the right
// state view based on hook status. All values shown on screen come from the
// API response transformed inside useDashboardData.
// ─────────────────────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const toastRef = useRef(null);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return saved ? saved === 'dark' : true;
  });

  // Active service for the chart-level filter strip — purely UI state, does
  // not trigger a refetch (we already have the per-service data on hand).
  const [activeServiceId, setActiveServiceId] = useState(ALL_SERVICES);

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const showWarn = useCallback((message) => {
    toastRef.current?.show({
      style: { fontFamily: 'Cairo', fontSize: '1rem' },
      life: 4000,
      severity: 'warn',
      summary: STRINGS.warningTitle,
      detail: message,
    });
  }, []);

  const showError = useCallback((message) => {
    toastRef.current?.show({
      style: { fontFamily: 'Cairo', fontSize: '1rem' },
      life: 5000,
      severity: 'error',
      summary: STRINGS.errorToastTitle,
      detail: message,
    });
  }, []);

  const {
    dateRange,
    setDateRange,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    services,
    selectedServices,
    setSelectedServices,
    loadingServices,
    paymentTypes,
    selectedPaymentTypes,
    setSelectedPaymentTypes,
    loadingPaymentTypes,
    data,
    errorMessage,
    isLoading,
    isReady,
    isEmpty,
    isError,
    isIdle,
    fetchData,
    goToPrevious,
    goToNext,
    goToToday,
    canGoNext,
    isAtCurrentPeriod,
    periodLabel,
  } = useDashboardData({ onWarn: showWarn, onError: showError });

  const showNavigator = dateRange !== 'custom' && !!periodLabel;

  const renderBody = () => {
    if (isLoading) return <LoadingState />;
    if (isError) return <ErrorState message={errorMessage} onRetry={fetchData} />;
    if (isEmpty) return <EmptyState />;
    if (isIdle || !data) return <IdleState />;
    if (!isReady) return <IdleState />;

    return (
      <>
        <KPIGrid data={data} />
        <ServiceFilterStrip
          data={data}
          activeServiceId={activeServiceId}
          onChange={setActiveServiceId}
        />
        <MainChart
          data={data}
          isDarkMode={isDarkMode}
          periodLabel={periodLabel}
          dateRange={dateRange}
          activeServiceId={activeServiceId}
        />
        <div className="main-dashboard-grid">
          <PaymentDistribution data={data} isDarkMode={isDarkMode} />
          <TrendChart data={data} isDarkMode={isDarkMode} />
        </div>
        <AdditionalAnalytics data={data} />
      </>
    );
  };

  return (
    <div className="page-wrapper" dir="rtl">
      <Toast ref={toastRef} position="top-center" style={{ margin: '0 1rem 0 0', fontFamily: 'Cairo' }} />
      <div className={`dashboard-root theme-${isDarkMode ? 'dark' : 'light'} rtl fade-in`}>
        <DashboardHeader
          isDarkMode={isDarkMode}
          onToggleTheme={() => setIsDarkMode((v) => !v)}
        />
        <FilterPanel
          isDarkMode={isDarkMode}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          services={services}
          selectedServices={selectedServices}
          onServicesChange={setSelectedServices}
          loadingServices={loadingServices}
          paymentTypes={paymentTypes}
          selectedPaymentTypes={selectedPaymentTypes}
          onPaymentTypesChange={setSelectedPaymentTypes}
          loadingPaymentTypes={loadingPaymentTypes}
          onFetch={fetchData}
          isFetching={isLoading}
        />

        {showNavigator && (
          <div className="period-nav-bar mb-4">
            <PeriodNavigator
              label={periodLabel}
              onPrevious={goToPrevious}
              onNext={goToNext}
              onToday={goToToday}
              canGoNext={canGoNext}
              isAtCurrentPeriod={isAtCurrentPeriod}
              disabled={isLoading}
            />
          </div>
        )}

        {renderBody()}
      </div>
    </div>
  );
};

export default AdminDashboard;
